import { useState, useCallback, useMemo } from "react";
import {
  Grid,
  GridColumn as Column,
  GridColumnMenuCheckboxFilter,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { process } from "@progress/kendo-data-query";

import {
  useQueryGetBrokerUsersList,
  useMutateDeleteBrokerUser,
} from "../../data/services/brokerUserService";

import DeleteDialog from "../../components/DeleteDialog";
import ErrorBanner from "../../components/ErrorBanner";
import LoadingIndicator from "../../components/LoadingIndicator";
import AddBrokerUser from "./AddBrokerUser";
import BrokerImportModal from "./import/BrokerImportModal";

const formatAssignmentRole = (assignmentRole) => {
  switch (assignmentRole) {
    case "primary":
      return "Primary";
    case "secondary":
      return "Secondary";
    case "ga":
      return "General Agent";
    case "manual":
      return "Manual";
    default:
      return "Unspecified";
  }
};

const BrokerUserList = () => {
  const [openEditForm, setOpenEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editItem, setEditItem] = useState();
  const [quickFilter, setQuickFilter] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);

  const [gridDataState, setGridDataState] = useState({
    take: 20,
    skip: 0,
  });

  const pagerSetting = {
    buttonCount: 5,
    info: true,
    type: "numeric",
    pageSizes: [10, 20, 50, 100, 200, 500],
    previousNext: true,
  };

  const {
    isLoading: brokerUserApiLoading,
    data: brokerUserApiResponse,
    refetch: refetchBrokerUsers,
  } = useQueryGetBrokerUsersList();

  const useMutateDeleteBrokerUserHandler = useMutateDeleteBrokerUser();

  const applyQuickFilter = useCallback((items, filterText) => {
    const normalizedFilter = (filterText || "").trim().toLowerCase();
    if (!normalizedFilter) return items;

    return items.filter((item) => {
      const clientText = (item.clients || [])
        .map((client) => `${client.clientId} ${client.clientName || ""}`)
        .join(" ")
        .toLowerCase();

      const searchableText = [
        item.userId,
        item.firstName,
        item.lastName,
        item.userEmail,
        item.phone,
        item.agency,
        clientText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedFilter);
    });
  }, []);

  const processedGridData = useMemo(() => {
    if (!brokerUserApiResponse?.data) return { data: [], total: 0 };

    const filteredGlobal = applyQuickFilter(
      brokerUserApiResponse.data,
      quickFilter,
    );

    const dataByTab = filteredGlobal.filter((item) => {
      const isInternal = item.userType === "INTERNAL";
      return selectedTab === 1 ? isInternal : !isInternal;
    });

    return process(dataByTab, gridDataState);
  }, [
    brokerUserApiResponse,
    quickFilter,
    selectedTab,
    gridDataState,
    applyQuickFilter,
  ]);

  if (brokerUserApiLoading) {
    return <LoadingIndicator />;
  }

  const handleCancelEdit = () => setOpenEditForm(false);

  const handleSubmit = async () => {
    try {
      await refetchBrokerUsers();
    } finally {
      setOpenEditForm(false);
    }
  };

  const handleOpenDeleteWindow = (item) => {
    setEditItem(item);
    setShowDeleteDialog(true);
  };

  const handleOpenEditWindow = (item) => {
    setEditItem(item);
    setOpenEditForm(true);
  };

  const handleOpenAddWindow = () => {
    setEditItem({ isInternalContext: selectedTab === 1 });
    setOpenEditForm(true);
  };

  const handleDelete = async (item) => {
    await useMutateDeleteBrokerUserHandler.mutateAsync({ userId: item.userId });
    setShowDeleteDialog(false);
  };

  const handleTabSelect = (e) => {
    setSelectedTab(e.selected);
    setGridDataState((prev) => ({ ...prev, skip: 0 }));
  };

  const ActionCell = (props) => (
    <td>
      <Button
        themeColor="primary"
        style={{ marginLeft: 10 }}
        onClick={() => handleOpenEditWindow(props.dataItem)}
      >
        Edit
      </Button>
      <Button
        themeColor="light"
        style={{ marginLeft: 10 }}
        onClick={() => handleOpenDeleteWindow(props.dataItem)}
      >
        Delete
      </Button>
    </td>
  );

  const ClientsCell = (props) => {
    const clients = props.dataItem.clients || [];
    if (props.dataItem.userType === "INTERNAL") {
      return (
        <td>
          <span style={{ color: "#888", fontStyle: "italic" }}>
            Global / Internal Access
          </span>
        </td>
      );
    }

    const roleSummary = clients.reduce((summary, client) => {
      const label = formatAssignmentRole(client.assignmentRole);
      summary[label] = (summary[label] || 0) + 1;
      return summary;
    }, {});

    const roleSummaryText = Object.entries(roleSummary)
      .map(([label, count]) => `${count} ${label}`)
      .join(", ");

    const clientTitle = clients
      .map((client) => {
        const role = formatAssignmentRole(client.assignmentRole);
        const src =
          client.assignmentSource === "salesforce" ? " [SF]" : " [Portal]";
        return `${client.clientId} (${role}${src})`;
      })
      .join(", ");

    return (
      <td title={clientTitle}>
        <div>{`${clients.length} Clients`}</div>
        {roleSummaryText && (
          <div style={{ fontSize: 12, color: "#666" }}>{roleSummaryText}</div>
        )}
      </td>
    );
  };

  const ColumnCheckboxMenu = (props) => (
    <GridColumnMenuCheckboxFilter
      {...props}
      data={brokerUserApiResponse?.data || []}
      expanded={true}
    />
  );

  const SharedGrid = () => (
    <div style={{ marginTop: 15 }}>
      <Grid
        sortable={true}
        pageable={pagerSetting}
        data={processedGridData}
        {...gridDataState}
        onDataStateChange={(e) => setGridDataState(e.dataState)}
      >
        <Column
          field="userId"
          title="User Id"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
        />
        <Column
          field="firstName"
          title="First Name"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
        />
        <Column
          field="lastName"
          title="Last Name"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
        />
        <Column
          field="userEmail"
          title="Email"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
        />
        <Column
          field="phone"
          title="Phone"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
        />
        <Column
          field="agency"
          title="Agency"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
        />
        <Column
          field="clients"
          title="Clients"
          cell={ClientsCell}
          sortable={false}
        />
        <Column
          field="Action"
          title="Action"
          cell={ActionCell}
          sortable={false}
        />
      </Grid>
    </div>
  );

  return (
    <div>
      <div
        style={{
          justifyContent: "space-between",
          display: "flex",
          paddingBottom: 10,
        }}
      >
        <div>
          <h2 style={{ margin: "10px" }}>Manage Portal Users</h2>
        </div>
        <div style={{ alignSelf: "center", display: "flex", gap: 10 }}>
          <input
            type="text"
            placeholder={
              selectedTab === 1
                ? "Search internal users..."
                : "Search brokers..."
            }
            value={quickFilter}
            onChange={(event) => {
              setQuickFilter(event.target.value);
              setGridDataState((currentState) => ({
                ...currentState,
                skip: 0,
              }));
            }}
            style={{
              width: 260,
              height: 32,
              border: "1px solid #c5c5c5",
              borderRadius: 4,
              padding: "0 10px",
            }}
          />
          <Button onClick={handleOpenAddWindow} themeColor={"success"}>
            Add User
          </Button>
          <Button onClick={() => setShowImportModal(true)} themeColor={"primary"}>
            Import
          </Button>
        </div>
      </div>

      <ErrorBanner
        handler={useMutateDeleteBrokerUserHandler}
        onError={() => setShowDeleteDialog(false)}
      />

      <TabStrip selected={selectedTab} onSelect={handleTabSelect}>
        <TabStripTab title="Brokers">
          <SharedGrid />
        </TabStripTab>
        <TabStripTab title="Internal (BDM / BAM)">
          <SharedGrid />
        </TabStripTab>
      </TabStrip>

      {openEditForm && (
        <AddBrokerUser
          onClose={handleCancelEdit}
          onSubmit={handleSubmit}
          item={editItem}
        />
      )}
      {showDeleteDialog && (
        <DeleteDialog
          onDelete={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
          item={editItem}
        />
      )}
      {showImportModal && (
        <BrokerImportModal
          onClose={() => {
            setShowImportModal(false);
            refetchBrokerUsers();
          }}
        />
      )}
    </div>
  );
};

export default BrokerUserList;
